import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslations } from 'use-intl';
import type { BulkResult } from '@shared/schemas';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import { api } from '@/lib/api';
import {
  IMPORT_FIELDS,
  customValues,
  findHeaderRow,
  guessMapping,
  mapRow,
  parseDelimited,
  proposeCustomColumns,
  type CustomColumn,
  type ImportField,
  type Row,
} from '@/lib/importer';
import { looksSensitive } from '@shared/schemas';
import { readSpreadsheetFile } from '@/lib/readSpreadsheet';
import { useConfig, useTeams } from '@/app/queries';
import { useQueryClient } from '@tanstack/react-query';
import { getActiveOrg } from '@/lib/api';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea } from '@/ui/Field';
import { Notice, PageHeader, Panel } from '@/ui/Panel';

type Stage = 'choose' | 'map' | 'done';

/**
 * Three steps, because a squad list is never quite the shape you expect:
 * read the file, confirm what each column means, then import.
 *
 * A row with a readable name always imports. An unreadable date or shirt number
 * leaves that one cell empty and says so — refusing the whole player over a
 * malformed cell would send the coach back to the spreadsheet for nothing.
 */
export function Import({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const config = useConfig();
  const qc = useQueryClient();

  const [stage, setStage] = useState<Stage>('choose');
  const [rows, setRows] = useState<Row[]>([]);
  const [headerRow, setHeaderRow] = useState(0);
  const [mapping, setMapping] = useState<Partial<Record<ImportField, number>>>({});
  const [teamId, setTeamId] = useState<string>('');
  const [pasted, setPasted] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);
  // Columns this product has no field for. Kept as the club's own fields rather
  // than dropped, because the coach chose this file for a reason.
  const [custom, setCustom] = useState<CustomColumn[]>([]);

  const teamList = teams.data ?? [];
  const activeTeam = teamId || teamList[0]?.id || '';

  const accept = (parsed: Row[]) => {
    if (parsed.length === 0) {
      setError(t('importer.noRows'));
      return;
    }
    const header = findHeaderRow(parsed);
    const guessed = guessMapping(parsed[header] ?? []);
    setRows(parsed);
    setHeaderRow(header);
    setMapping(guessed);
    setCustom(proposeCustomColumns(parsed[header] ?? [], parsed.slice(header + 1), guessed));
    setError(null);
    setStage('map');
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      accept(await readSpreadsheetFile(file));
    } catch {
      setError(t('importer.noRows'));
    } finally {
      setBusy(false);
    }
  };

  const headers = rows[headerRow] ?? [];
  const dataRows = useMemo(() => rows.slice(headerRow + 1), [rows, headerRow]);

  const positions = config.data?.positions ?? [];
  const mapped = useMemo(
    () => dataRows.map((row, i) => mapRow(row, mapping, i + 1, positions)),
    [dataRows, mapping, positions],
  );

  // A row without a usable name cannot become a player; everything else can.
  const importable = mapped.filter(
    (m) => m.values.firstName && m.values.lastName,
  );

  const runImport = async () => {
    setBusy(true);
    setError(null);
    try {
      const keeping = custom.filter((c) => c.keep && c.label.trim());
      const payload = {
        teamId: activeTeam || undefined,
        players: importable.map((m) => ({
          ...m.values,
          attributes: customValues(dataRows[m.row - 1] ?? [], keeping),
        })),
        fields: keeping.map((c) => ({ label: c.label.trim() })),
      };
      const res = await api.post<BulkResult>('/players/import', payload);
      setResult(res);
      setStage('done');
      qc.invalidateQueries({ queryKey: ['players', getActiveOrg()] });
      qc.invalidateQueries({ queryKey: ['teams', getActiveOrg()] });
    } catch {
      setError(t('error.generic'));
    } finally {
      setBusy(false);
    }
  };

  const issueLabel = (code: string) => {
    const key = `importer.issue.${code}`;
    const translated = t(key);
    return translated === key ? code : translated;
  };

  return (
    <>
      <PageHeader
        title={t('importer.title')}
        lead={t('importer.lead')}
        help={localePath(locale, '/guide/squad')}
      />

      {error ? (
        <div className="mb-4">
          <Notice tone="risk">{error}</Notice>
        </div>
      ) : null}

      {stage === 'choose' ? (
        <Panel title={t('importer.dropTitle')}>
          <div className="grid gap-5 p-4">
            <p className="max-w-[70ch] text-ink-muted">{t('importer.dropBody')}</p>

            <label className="inline-flex w-fit cursor-pointer items-center rounded-(--radius-control) border border-line-strong bg-surface px-4 py-2.5 font-medium hover:border-ink">
              {busy ? <Spinner className="me-2" /> : null}
              {t('importer.chooseFile')}
              <input
                type="file"
                className="sr-only"
                accept=".csv,.tsv,.txt,.xlsx,.xlsm,.xls,.ods"
                onChange={(e) => void onFile(e.target.files?.[0])}
              />
            </label>

            <Field label={t('importer.pasteLabel')}>
              {(p) => (
                <TextArea
                  {...p}
                  value={pasted}
                  onChange={(e) => setPasted(e.target.value)}
                  placeholder={t('importer.pastePlaceholder')}
                  className="min-h-40 font-mono text-meta"
                />
              )}
            </Field>

            <Button
              variant="primary"
              className="w-fit"
              disabled={!pasted.trim()}
              onClick={() => accept(parseDelimited(pasted))}
            >
              {t('importer.readList')}
            </Button>
          </div>
        </Panel>
      ) : null}

      {stage === 'map' ? (
        <div className="grid gap-5">
          <Panel title={t('importer.mapping')}>
            <div className="grid gap-4 p-4">
              <p className="max-w-[70ch] text-ink-muted">{t('importer.mappingLead')}</p>

              <div className="flex flex-wrap items-end gap-4">
                <div className="w-40">
                  <Field label={t('importer.headerRow')}>
                    {(p) => (
                      <SelectInput
                        {...p}
                        value={String(headerRow)}
                        onChange={(e) => {
                          const next = Number(e.target.value);
                          setHeaderRow(next);
                          setMapping(guessMapping(rows[next] ?? []));
                        }}
                      >
                        {rows.slice(0, 10).map((r, i) => (
                          <option key={i} value={i}>
                            {t('importer.row')} {i + 1}: {r.slice(0, 3).join(' · ').slice(0, 40)}
                          </option>
                        ))}
                      </SelectInput>
                    )}
                  </Field>
                </div>

                {teamList.length > 0 ? (
                  <div className="w-56">
                    <Field label={t('importer.intoTeam')}>
                      {(p) => (
                        <SelectInput
                          {...p}
                          value={activeTeam}
                          onChange={(e) => setTeamId(e.target.value)}
                        >
                          {teamList.map((tm) => (
                            <option key={tm.id} value={tm.id}>
                              {tm.name}
                            </option>
                          ))}
                        </SelectInput>
                      )}
                    </Field>
                  </div>
                ) : null}
              </div>

              <div className="grid gap-x-5 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                {IMPORT_FIELDS.map((field) => (
                  <Field key={field} label={t(`squad.${fieldLabelKey(field)}`)}>
                    {(p) => (
                      <SelectInput
                        {...p}
                        value={mapping[field] === undefined ? '' : String(mapping[field])}
                        onChange={(e) => {
                          const index = e.target.value === '' ? undefined : Number(e.target.value);
                          setMapping((m) => {
                            const next = { ...m, [field]: index };
                            // A column claimed by a real field is no longer a
                            // custom one, and vice versa when it is released.
                            setCustom((current) =>
                              proposeCustomColumns(headers, dataRows, next).map((proposed) => {
                                const kept = current.find((c) => c.index === proposed.index);
                                return kept ?? proposed;
                              }),
                            );
                            return next;
                          });
                        }}
                      >
                        <option value="">{t('importer.ignore')}</option>
                        {headers.map((h, i) => (
                          <option key={i} value={i}>
                            {h || `${t('importer.row')} ${i + 1}`}
                          </option>
                        ))}
                      </SelectInput>
                    )}
                  </Field>
                ))}
              </div>

              {custom.length > 0 ? (
                <div className="border-t border-line pt-5">
                  <h3 className="font-condensed text-subhead font-semibold tracking-tight">
                    {t('importer.customTitle')}
                  </h3>
                  <p className="mt-1 max-w-[72ch] text-meta text-ink-muted">
                    {t('importer.customBody')}
                  </p>
                  <ul className="mt-3 grid gap-2">
                    {custom.map((column) => {
                      const sample = dataRows.find((r) => (r[column.index] ?? '').trim())?.[
                        column.index
                      ];
                      const sensitive = looksSensitive(column.label);
                      return (
                        <li
                          key={column.index}
                          className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line pb-2"
                        >
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={column.keep}
                              onChange={(e) =>
                                setCustom((c) =>
                                  c.map((x) =>
                                    x.index === column.index
                                      ? { ...x, keep: e.target.checked }
                                      : x,
                                  ),
                                )
                              }
                              className="size-4 accent-ink"
                            />
                            <span className="sr-only">{t('importer.keepColumn')}</span>
                          </label>
                          <input
                            value={column.label}
                            onChange={(e) =>
                              setCustom((c) =>
                                c.map((x) =>
                                  x.index === column.index ? { ...x, label: e.target.value } : x,
                                ),
                              )
                            }
                            aria-label={t('importer.fieldName')}
                            disabled={!column.keep}
                            className="min-h-9 w-48 rounded-(--radius-control) border border-line bg-surface px-2 py-1 text-meta disabled:opacity-50"
                          />
                          <span className="min-w-0 flex-1 truncate text-meta text-ink-faint">
                            {headers[column.index] || t('importer.noHeading')}
                            {sample ? ` · ${sample}` : ''}
                          </span>
                          {sensitive && column.keep ? (
                            <span className="text-meta text-warn">{t('importer.sensitive')}</span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>
          </Panel>

          <Panel
            title={t('importer.preview')}
            count={`${importable.length} / ${mapped.length}`}
            action={
              <Button variant="primary" busy={busy} onClick={() => void runImport()}>
                {t('importer.importNow', { count: importable.length })}
              </Button>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[40rem] text-meta">
                <thead>
                  <tr className="border-b border-line bg-surface-sunken text-ink-faint">
                    <th className="px-3 py-2 text-start font-normal">{t('squad.number')}</th>
                    <th className="px-3 py-2 text-start font-normal">{t('squad.player')}</th>
                    <th className="px-3 py-2 text-start font-normal">{t('squad.position')}</th>
                    <th className="px-3 py-2 text-start font-normal">{t('squad.dateOfBirth')}</th>
                    <th className="px-3 py-2 text-start font-normal">{t('squad.strongFoot')}</th>
                    <th className="px-3 py-2 text-start font-normal" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {mapped.slice(0, 60).map((m) => {
                    const usable = Boolean(m.values.firstName && m.values.lastName);
                    return (
                      <tr key={m.row} className={usable ? '' : 'bg-surface-sunken text-ink-faint'}>
                        <td className="tabular px-3 py-2">{String(m.values.shirtNumber ?? '–')}</td>
                        <td className="px-3 py-2">
                          {usable
                            ? `${m.values.firstName} ${String(m.values.lastName).toLocaleUpperCase()}`
                            : t('importer.noName')}
                        </td>
                        <td className="px-3 py-2">{String(m.values.primaryPositionKey ?? '–')}</td>
                        <td className="tabular px-3 py-2">{String(m.values.dateOfBirth ?? '–')}</td>
                        <td className="px-3 py-2">{String(m.values.strongFoot ?? '–')}</td>
                        <td className="px-3 py-2">
                          {m.issues.length > 0 ? (
                            <span className="text-warn">
                              {m.issues.map(issueLabel).join(' · ')}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      ) : null}

      {stage === 'done' && result ? (
        <Panel title={t('importer.doneTitle')}>
          <div className="grid gap-4 p-4">
            <p className="text-ink-muted">
              {t('importer.doneBody', { created: result.created, failed: result.failed })}
            </p>
            {result.failed > 0 ? (
              <ul className="grid gap-1 text-meta text-warn">
                {result.results
                  .filter((r) => !r.ok)
                  .slice(0, 20)
                  .map((r) => (
                    <li key={r.row}>
                      {t('importer.row')} {r.row}: {issueLabel(r.error ?? 'import_failed')}
                    </li>
                  ))}
              </ul>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Link
                to={localePath(locale, '/squad')}
                className="inline-flex min-h-11 items-center rounded-(--radius-control) bg-ink px-4 font-medium text-paper hover:bg-ink-muted"
              >
                {t('importer.viewSquad')}
              </Link>
              <Button
                variant="quiet"
                onClick={() => {
                  setStage('choose');
                  setRows([]);
                  setPasted('');
                  setResult(null);
                }}
              >
                {t('importer.startOver')}
              </Button>
            </div>
          </div>
        </Panel>
      ) : null}

      {config.isLoading ? null : null}
    </>
  );
}

/** Import fields reuse the squad form's labels rather than a second vocabulary. */
function fieldLabelKey(field: ImportField): string {
  const map: Record<ImportField, string> = {
    fullName: 'fullName',
    firstName: 'firstName',
    lastName: 'lastName',
    knownAs: 'knownAs',
    shirtNumber: 'shirtNumber',
    dateOfBirth: 'dateOfBirth',
    nationality: 'nationality',
    strongFoot: 'strongFoot',
    heightCm: 'heightCm',
    weightKg: 'weightKg',
    primaryPositionKey: 'primaryPosition',
    secondaryPositionKey: 'secondaryPosition',
    statusKey: 'status',
  };
  return map[field];
}
